/** Pure progress-threshold policy + crossing tracker for signage clients. */

export const DEFAULT_PROGRESS_THRESHOLDS = [25, 50, 75, 100] as const;

export function progressCelebrationTargetAmount(
  goalMinorUnits: number,
  percent: number
): number {
  if (!Number.isFinite(goalMinorUnits) || goalMinorUnits <= 0) return 0;
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  return Math.ceil((goalMinorUnits * percent) / 100);
}

function positiveMilestoneAmount(milestone: Record<string, unknown>): number {
  const raw = milestone['amount_minor_units'] ?? milestone['amount'];
  if (typeof raw === 'boolean') return 0;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function normalizeThresholds(values: readonly unknown[]): number[] {
  return Array.from(
    new Set(
      values
        .map(Number)
        .map((value) => Math.round(value))
        .filter((value) => Number.isFinite(value) && value > 0 && value <= 100)
    )
  ).sort((a, b) => a - b);
}

export function resolveProgressCelebrationThresholds(
  content: Record<string, unknown>,
  milestones: ReadonlyArray<Record<string, unknown>>
): number[] {
  const goal = Number(content['goal_minor_units'] ?? content['goal_amount_minor_units'] ?? 0);
  if (!Number.isFinite(goal) || goal <= 0) return [];

  const supplied = Array.isArray(content['celebration_thresholds'])
    ? normalizeThresholds(content['celebration_thresholds'] as unknown[])
    : [];
  if (supplied.length > 0) {
    return normalizeThresholds([...supplied, 100]);
  }

  const hasConfiguredMilestone = milestones.some((milestone) => positiveMilestoneAmount(milestone) > 0);
  return hasConfiguredMilestone ? [100] : [...DEFAULT_PROGRESS_THRESHOLDS];
}

export class ProgressCelebrationTracker {
  private contextKey: string | null = null;
  private goalMinorUnits = 0;
  private previousPercent = 0;
  private readonly fired = new Set<number>();
  private activeThresholds = new Set<number>();
  private primed = false;

  prime(
    contextKey: string,
    progressPercent: number,
    goalMinorUnits: number,
    thresholds: readonly number[]
  ): void {
    this.contextKey = contextKey;
    this.goalMinorUnits = goalMinorUnits;
    this.previousPercent = Math.max(0, Number(progressPercent) || 0);
    this.fired.clear();
    this.activeThresholds = new Set(normalizeThresholds(thresholds));
    for (const threshold of this.activeThresholds) {
      if (this.previousPercent >= threshold) this.fired.add(threshold);
    }
    this.primed = true;
  }

  diff(
    contextKey: string,
    progressPercent: number,
    goalMinorUnits: number,
    thresholds: readonly number[]
  ): number[] {
    const current = Math.max(0, Number(progressPercent) || 0);
    const normalized = normalizeThresholds(thresholds);
    if (goalMinorUnits <= 0) {
      this.prime(contextKey, 0, goalMinorUnits, []);
      return [];
    }
    if (!this.primed || this.contextKey !== contextKey || this.goalMinorUnits !== goalMinorUnits) {
      this.prime(contextKey, current, goalMinorUnits, normalized);
      return [];
    }

    const nextActive = new Set(normalized);
    for (const threshold of nextActive) {
      if (!this.activeThresholds.has(threshold) && current >= threshold) {
        this.fired.add(threshold);
      }
    }
    this.activeThresholds = nextActive;

    const crossed = normalized.filter(
      (threshold) =>
        this.previousPercent < threshold &&
        current >= threshold &&
        !this.fired.has(threshold)
    );
    for (const threshold of crossed) this.fired.add(threshold);
    this.previousPercent = current;
    return crossed;
  }
}

export function progressCelebrationTitle(percent: number): string {
  if (percent >= 100) return 'We reached our goal!';
  if (percent >= 75) return 'Three quarters of the way there!';
  if (percent >= 50) return 'We are halfway there!';
  return 'A quarter of the way there!';
}
